import { getListingEcolabels } from '../../../config/ecolabels';

// Products in the "Featured products" row.
export const FEATURED_PRODUCT_COUNT = 12;

// How much each signal is worth. A listing's base score sits roughly between 0
// and 2; the diversity penalties are on the same scale, so a vendor's second
// product only beats another vendor's first when it is clearly the better pick.
const WEIGHTS = {
  // Newer products rank higher, but only gently: the pool is already the newest
  // listings, and freshness shouldn't drown out variety.
  freshness: 0.6,
  // ethy's whole point is verified credentials, so products carrying more
  // ecolabels are a better showcase (capped so one heavily-labelled product
  // can't dominate).
  ecolabels: 0.6,
  // A product without a photo makes a poor first impression. It's still allowed
  // in, so a thin catalogue doesn't leave the row empty.
  missingImage: -1.5,
  // Random-but-stable nudge that changes once a day, so returning shoppers see
  // a different mix rather than the exact same row every visit.
  rotation: 0.35,
};

// Penalties applied for each product already picked that shares a trait.
const PENALTIES = {
  // Seeing a row of one brand's range is the main thing this logic avoids.
  sameVendor: 1.2,
  // Mixing categories (fashion, beauty, home…) makes the row feel browsable.
  sameCategory: 0.5,
  // Extra cost for sitting right next to the same vendor or category, so even
  // when repeats are unavoidable (few vendors today) they're spread out.
  adjacentVendor: 1.0,
  adjacentCategory: 0.3,
};

const MAX_COUNTED_ECOLABELS = 4;

// FNV-1a: a small, fast string hash. Turned into a number in [0, 1) so it can
// serve as a deterministic "random" value for a listing on a given day.
const hashToUnit = str => {
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0) / 0x100000000;
};

const isVisibleAuthor = author => !author?.attributes?.deleted && !author?.attributes?.banned;

// The top-level category a listing is filed under, e.g. publicData.categoryLevel1.
const getCategory = (listing, categoryKey) =>
  listing?.attributes?.publicData?.[`${categoryKey}1`] || null;

/**
 * Today's rotation seed. Uses the UTC date so the server-rendered page and the
 * browser agree on the same mix, and the row refreshes once a day.
 *
 * @param {Date} [date]
 * @returns {string}
 */
export const dailySeed = (date = new Date()) => date.toISOString().slice(0, 10);

/**
 * Choose and order the products for the "Featured products" row.
 *
 * Rather than simply showing the newest listings (which, when one vendor
 * publishes a batch, turns the row into a wall of that vendor's range), every
 * candidate gets a quality score and the row is built one slot at a time,
 * always taking the candidate whose score is highest *after* penalties for
 * repeating a vendor or category that's already in the row.
 *
 * This copes with both ends of the marketplace's life:
 * - With only a few vendors or products, nothing is excluded — repeats are
 *   simply spread out (vendor A, vendor B, vendor A…) so the row stays full.
 * - With lots of vendors and products, the row naturally becomes one product
 *   per vendor across a mix of categories, favouring fresh, photographed
 *   products with more verified ecolabels, and rotating daily.
 *
 * The result is deterministic for a given pool and seed, so it renders the same
 * on the server and in the browser.
 *
 * @param {Array<Object>} listings candidate listings, newest first
 * @param {Object} [options]
 * @param {number} [options.count] how many products to return
 * @param {string} [options.categoryKey] category param prefix, e.g. 'categoryLevel'
 * @param {string} [options.seed] rotation seed, e.g. dailySeed()
 * @returns {Array<Object>}
 */
export const pickFeaturedProducts = (listings = [], options = {}) => {
  const { count = FEATURED_PRODUCT_COUNT, categoryKey = 'categoryLevel', seed = '' } = options;

  // Drop duplicates and anything a shopper shouldn't be shown.
  const seen = new Set();
  const eligible = listings.filter(listing => {
    const uuid = listing?.id?.uuid;
    const attributes = listing?.attributes || {};
    const ok =
      uuid &&
      !seen.has(uuid) &&
      !attributes.deleted &&
      (!attributes.state || attributes.state === 'published') &&
      isVisibleAuthor(listing.author);
    if (ok) {
      seen.add(uuid);
    }
    return ok;
  });

  const poolSize = eligible.length;
  const candidates = eligible.map((listing, index) => {
    const uuid = listing.id.uuid;
    const ecolabelCount = getListingEcolabels(listing.attributes?.publicData).length;
    const hasImage = listing.images?.length > 0;

    const freshness = poolSize > 1 ? 1 - index / (poolSize - 1) : 1;
    const score =
      WEIGHTS.freshness * freshness +
      WEIGHTS.ecolabels * (Math.min(ecolabelCount, MAX_COUNTED_ECOLABELS) / MAX_COUNTED_ECOLABELS) +
      (hasImage ? 0 : WEIGHTS.missingImage) +
      WEIGHTS.rotation * hashToUnit(`${seed}:${uuid}`);

    return {
      listing,
      score,
      // Listings without a known vendor/category can't be told apart, so they
      // each get a bucket of their own rather than all counting as one.
      vendor: listing.author?.id?.uuid || `listing:${uuid}`,
      category: getCategory(listing, categoryKey) || `listing:${uuid}`,
    };
  });

  const vendorCounts = {};
  const categoryCounts = {};
  const picked = [];
  let remaining = candidates;

  while (picked.length < count && remaining.length > 0) {
    const previous = picked[picked.length - 1];

    let best = null;
    let bestValue = -Infinity;
    remaining.forEach(candidate => {
      const value =
        candidate.score -
        PENALTIES.sameVendor * (vendorCounts[candidate.vendor] || 0) -
        PENALTIES.sameCategory * (categoryCounts[candidate.category] || 0) -
        (previous?.vendor === candidate.vendor ? PENALTIES.adjacentVendor : 0) -
        (previous?.category === candidate.category ? PENALTIES.adjacentCategory : 0);
      // Strictly greater keeps ties in pool (newest-first) order.
      if (value > bestValue) {
        best = candidate;
        bestValue = value;
      }
    });

    picked.push(best);
    vendorCounts[best.vendor] = (vendorCounts[best.vendor] || 0) + 1;
    categoryCounts[best.category] = (categoryCounts[best.category] || 0) + 1;
    remaining = remaining.filter(candidate => candidate !== best);
  }

  return picked.map(candidate => candidate.listing);
};
